import React, { useEffect, useState } from 'react';
import { default as Select, components } from "react-select";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useCompany, useCompanyPermission } from '../../component/CompanySelection/CompanyContext';
import { getUsers, getUserUUID } from '../../service/UserService';
import Has from '../../component/Permission/Has';
import LoadingSpinner from '../../component/LoadingSpinner/LoadingSpinner';
import { FF_USERS_PAGE } from '../../config/FeatureFlagTypes';
import { U_USER_ADMIN, U_VIEW_USERS } from '../../config/PermissionTypes';
import { getUrl } from '../../util/EfupUtility';
import { USER_NAME_REGEX } from '../../validators/InputValidator';
import {USER_SIDE_PANEL} from './../../config/SidePanelTypes';
import SidePanel from './../../component/SidePanel/SidePanel';
import './Users.css';

const customSelectStyles = {
    menu: (provided) => ({
        ...provided,
        marginTop: '5px'
    }),
    option: (styles, { isFocused }) => {
        return {
            ...styles,
            backgroundColor: isFocused ? '#00b5e2' : undefined,
            color: '#333333'
        }
    },
    control: (styles, { isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            boxShadow: isFocused ? '0 0 0 1px #00b5e2' : undefined,
            borderColor: isDisabled
                ? undefined
                : isSelected && !isFocused
                    ? '#00b5e2'
                    : isFocused
                        ? '#00b5e2'
                        : '#D3D6DE',
            ':hover': {
                borderColor: '#00b5e2',
            }
        }
    }
}

//this is needed so the label of 'Status' appears in the status dropdown
const Control = ({ children, ...props }) => {
    const label = props.selectProps.customProps.selectLabel;
    return (
        <components.Control {...props}>
            <div 
                className={`efup-filter-label cb-font-poppins`}
                data-testid="status-filter-control-label"
            >{label}</div> 
            <div className="efup-filter-value-container" data-testid="status-filter-control-values">{children}</div>
        </components.Control>
    );
};

export default function Users(props) {
    const [allUsers, setAllUsers] = useState([]);   //this will be master list that can be filtered & searched from
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userUuid, setUserUuid] = useState("");
    const [sortColumn, setSortColumn] = useState("name");
    const [sortDirection, setSortDirection] = useState("desc");
    const [searchValue, setSearchValue] = useState("");
    const [selectedStatus, setSelectedStatus] = useState({label: 'All', value: 'all'});
    const [currentUserUUID, setCurrentUserUUID] = useState("");
    const userStatusOptions = [
        { label: 'All', value: 'all'},
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];
    const { companyNumber } = useCompany();
    const navigate = useNavigate();
    
    let isEditClicked = false;
    function onRowSelect(userUuid) {
        if(isEditClicked === true){
            isEditClicked = false;
        } else {
            setUserUuid(userUuid);
            setIsModalOpen(true);
        }
    }
    
    function onEditLink() {
        isEditClicked = true;
    }

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        applyFilters(searchValue, status.value);
    }

    const handleSearchChange = (value) => {
        const regex = new RegExp(USER_NAME_REGEX);
        if (regex.test(value)) {
            setSearchValue(value);
            applyFilters(value, selectedStatus.value);
        }
    }

    const applyFilters = (searchValue, statusValue) => {
        let users = allUsers;
        if (searchValue !== "") {
            searchValue = searchValue.toLowerCase();
            users = users.filter(user => user.name.toLowerCase().includes(searchValue) || user.username.toLowerCase().includes(searchValue));
        }
        if (statusValue !== "all") {
            users = users.filter(user => user.is_active === statusValue);
        }
        setUsers(users);
    }

    //this sets the sort icon of the currently sorted column
    const iconClass = (column) => {
        const dir = sortColumn === column ? `_${sortDirection}ending` : '';
        return "cb-icon icon-sort" + dir;
    }

    const setSort = (column) => {
        let direction = 'desc';
        if (column) {
            if (column !== sortColumn) {
                setSortColumn(column);
                setSortDirection(direction);
            } else {
                direction = sortDirection === 'desc' ? 'asc' : 'desc';
                setSortDirection(direction)
            }
        }
        const val = direction === "asc" ? 1 : -1;
        users.sort((a, b) => (b[column].toString().localeCompare(a[column].toString())) * val);
    }

    const errorHandler = (error) => {
        if (error?.response?.status !== 200) {
            setHasError(true);
            setAllUsers([]);
            setUsers([]);
        } else {
            setHasError(false);
        }
        setIsLoading(false);
    }

    const dataHandler = (userData) => {
        setIsLoading(false);
        //initially sort users by name
        const users = userData.sort((a, b) => a.name.localeCompare(b.name));
        setAllUsers(users);
        setUsers(users);
    }

    const UUIDDataHandler = (uuid) => {
        setCurrentUserUUID(uuid.data);
    }

    useEffect(() => {
        getUsers(companyNumber, dataHandler, errorHandler);
        getUserUUID(UUIDDataHandler);
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    //if permissions are not loaded, show loading spinner
    const companyPermission = useCompanyPermission();
    if (!companyPermission) {
        return (
            <div id="dashboard-loader">
                <LoadingSpinner blue />
            </div>
        );
    }

    const columnHeaders = [{display:"Name", value:"name"},
        {display:"User ID", value:"username"},
        {display:"Role", value:"roleName"},
        {display:"Email", value:"email"},
        {display:"Status", value:"is_active"}];

    return (
        <React.Fragment>
            <Has isPageLevel={true} featureFlag={FF_USERS_PAGE} permission={U_VIEW_USERS}>
                {/* <!-- Page content--> */}
                <div className="container-fluid" data-testid="users-page" id="users-page">

                    <div className="page-title-area">
                        <h1 data-testid='users-page-header-title'>Users</h1>
                    </div>

                <div className="row users-table-runner">
                    <div className="col-md-6 user-filters">
                        <div>
                            <div className="sectionTitle" data-testid="filter-users-label">Filter Users</div>
                            <Select
                                className="cb-multiselect cb-form"
                                components={{ 
                                    IndicatorSeparator: () => null,
                                    Control,
                                }}
                                customProps={{ selectLabel: "Status:", selectType: "is_active" }}
                                isSearchable={false}
                                onChange={handleStatusChange}
                                options={userStatusOptions}
                                styles={customSelectStyles}
                                value={selectedStatus}
                            />
                        </div>
                        <div>
                            <div className="sectionTitle" data-testid="search-users-label">Search Users</div>
                            <div className="user-search-container">
                                <input className='cb-form-control'
                                    type="text"
                                    data-testid="user-search-input"
                                    maxLength="256"
                                    onChange={e => handleSearchChange(e.target.value)}
                                    placeholder="Name or User ID"
                                    value={searchValue}
                                />
                                <i className="cb-icon icon-search"></i>
                            </div>
                        </div>
                    </div>
                        <div className = "col-md-6">
                            <Has permission={U_USER_ADMIN}>
                                <div className= "add-user-button-container">
                                    <button
                                        type="button"
                                        className="cb-btn add-user-button d-lg-block"
                                        data-testid="add-user-button"
                                        onClick={() => navigate(getUrl(companyNumber, '/users/add'))}>
                                            + Add New User
                                        </button>
                                </div>
                            </Has>
                        </div>
                    </div>

                    { isLoading ? (
                        <div className="user-message" data-testid="users-loading">
                            <LoadingSpinner blue size={50} />
                            <p className='text-center'>Loading user data...</p>
                        </div>
                    ) : null }
                    { !isLoading && hasError ? (
                        <div className="user-message">
                            <h3 className="text-center" data-testid="user-data-error">An error occurred getting user data.</h3>
                        </div>
                    ) : null }
                    { !isLoading && !hasError && !users.length ? (
                        <div className="user-message">
                            <h3 className="text-center" data-testid="user-data-no-results">No user results found.</h3>
                        </div>
                    ) : null }
                    { !isLoading && !hasError && users.length ? (
                        <div className="cb-table-responsive users-table">
                            <table className="table table-sort table-arrows cb-table">
                                <thead className="table-light thead">
                                    <tr>
                                        {columnHeaders.map(item => {
                                                return <th tabIndex={0} onKeyDown={({key}) => key === "Enter" ? setSort(item.value) : ""}>
                                                    <span onClick={() => setSort(item.value)} id={`users-table-${item.value}-header`} data-testid={`users-table-${item.value}-header`}>
                                                        {item.display} <i className={iconClass(item.value)}></i>
                                                    </span>
                                                </th>
                                        })}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                            {users.map((user, index) => {
                                return (
                                    <tr key={`user-row-${index}`} onClick={() => onRowSelect(user.uuid)} tabIndex={0} onKeyDown={({key}) => key === "Enter" ? onRowSelect(user.uuid) : ""}>
                                        <td>{user.name}</td>
                                        <td>{user.username}</td>
                                        <td>{user.roleName}</td>
                                        <td>{user.email}</td>
                                        <td><span className={`status ${user.is_active ? 'active' : 'inactive'}`}>&nbsp;</span>{user.is_active ? 'Active' : 'Inactive'}</td>
                                        {currentUserUUID !== user.uuid ? <td className="text-right">
                                            <Has permission={U_USER_ADMIN}>
                                                <Link onKeyDown={({key}) => key === "Enter" ? onEditLink(companyNumber,user.username): ""}
                                                    className = "edit-cell"
                                                    to={getUrl(companyNumber, `/users/${user.username}/edit`)}
                                                    data-testid={`edit-user-link-${user.username}`}
                                                    state={{ companyNumber: companyNumber, username: user.username, userUuid: user.uuid }}>
                                                    <i className="cb-icon icon-edit"></i>
                                                    <span>Edit</span>
                                                </Link>
                                            </Has>
                                        </td>
                                        : <td></td> }
                                    </tr>
                                )
                            })}
                                </tbody>
                            </table>
                            <SidePanel
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                sidePanelType={USER_SIDE_PANEL}
                                userUuid={userUuid}
                                companyNumber={companyNumber} />
                        </div>
                    ) : null }
                </div>
            </Has>
        </React.Fragment>
    );
}